import React, { useEffect } from "react";
import * as Yup from "yup";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { createNewUser, resetState, selectIsBusy, selectIsCreated, selectNewUserProfile } from "./NewUserProfileSlice";
import { Permission } from "services/userSession/UserPermittedComponent";
import { Permissions } from "cpl-react-common/components/Enums/Permissions";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { useNavigate } from "react-router-dom";
import FormSwitch from "cpl-react-common/components/Inputs/FormSwitch";

const newUserSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  emailAddress: Yup.string().email("Email should be a valid email address").required("Email is required"),
  lastName: Yup.string().required("Last name is required"),
  telephoneNumber: Yup.string().required("Contact phone is required"),
  createCoreUser: Yup.boolean(),
});

const NewUserProfile: React.FC = () => {
  const thunkDispatch = useThunkDispatch();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const initialValues = useSelector(selectNewUserProfile);
  const isBusy = useSelector(selectIsBusy);

  const isCreated = useSelector(selectIsCreated);
  useEffect(() => {
    if (isCreated) {
      dispatch(resetState());
      const path = `/Admin/Users/Users`;
      navigate(path);
    }
  }, [isCreated, dispatch, navigate]);

  return (
    <Permission permissionPredicate={(permission) => permission === Permissions.AddUsers}>
      <div className="card mb-5 mb-xl-10">
        <ActivityIndicator isBusy={isBusy}>
          <div className="card-header">
            <div className="card-title">
              <h3>Create a new User</h3>
            </div>
          </div>
          <div className="card-body border-top p-9">
            <Formik
              enableReinitialize
              initialValues={initialValues}
              onSubmit={async (values, { resetForm }) => {
                await thunkDispatch(createNewUser({ user: values }));
                resetForm();
              }}
              validationSchema={newUserSchema}
            >
              {({ values, setFieldValue }) => (
                <Form>
                  <div className="row mb-6">
                    <label className="col-lg-4 col-form-label required fw-bold fs-6">First Name</label>
                    <div className="col-lg-8">
                      <div className="row">
                        <div className="col-lg-12 fv-row">
                          <Field
                            data-test-id="new-user-form__first-name-input"
                            name={"firstName"}
                            id="firstName"
                            className="form-control form-control-lg form-control-solid mb-3 mb-lg-0"
                            type="text"
                          />
                          <div className="fv-plugins-message-container">
                            <div className="fv-help-block">
                              <ErrorMessage name="firstName" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row mb-6">
                    <label className="col-lg-4 col-form-label required fw-bold fs-6">Last Name</label>
                    <div className="col-lg-8">
                      <div className="row">
                        <div className="col-lg-12 fv-row">
                          <Field
                            data-test-id="new-user-form__last-name-input"
                            name={"lastName"}
                            id="lastName"
                            className="form-control form-control-lg form-control-solid mb-3 mb-lg-0"
                            type="text"
                          />
                          <div className="fv-plugins-message-container">
                            <div className="fv-help-block">
                              <ErrorMessage name="lastName" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row mb-6">
                    <label className="col-lg-4 col-form-label required fw-bold fs-6">Email Address</label>
                    <div className="col-lg-8">
                      <div className="row">
                        <div className="col-lg-12 fv-row">
                          <Field
                            data-test-id="new-user-form__email-address-input"
                            name={"emailAddress"}
                            id="emailAddress"
                            className="form-control form-control-lg form-control-solid mb-3 mb-lg-0"
                            type="text"
                          />
                          <div className="fv-plugins-message-container">
                            <div className="fv-help-block">
                              <ErrorMessage name="emailAddress" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row mb-6">
                    <label className="col-lg-4 col-form-label required fw-bold fs-6">Contact Number</label>
                    <div className="col-lg-8">
                      <div className="row">
                        <div className="col-lg-12 fv-row">
                          <Field
                            data-test-id="new-user-form__telephone-number-input"
                            name={"telephoneNumber"}
                            id="telephoneNumber"
                            className="form-control form-control-lg form-control-solid mb-3 mb-lg-0"
                            type="text"
                          />
                          <div className="fv-plugins-message-container">
                            <div className="fv-help-block">
                              <ErrorMessage name="telephoneNumber" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row mb-6">
                    <div className="col-lg-4"></div>
                    <div className="col-lg-8 d-flex align-items-center">
                      <div className="row">
                        <div className="col-lg-12 fv-row">
                          <FormSwitch
                            data-test-id="new-user-form__create-core-user-switch"
                            setValue={(checked) => setFieldValue("createCoreUser", checked)}
                            label="Create a new Proptimo Core user"
                            classNames="me-1 fw-bold"
                            defaultChecked={values.createCoreUser}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <button data-test-id="new-user-form__submit-button" type="submit" className="btn btn-success">
                    Submit
                  </button>
                </Form>
              )}
            </Formik>
          </div>
        </ActivityIndicator>
      </div>
    </Permission>
  );
};

export default NewUserProfile;
